import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';

import { FeatureId, featureConfigs } from '../../config/featureConfig';
import { useLicenseEntitlement } from '../../hooks/useLicenseEntitlement';

import LockedFeatureOverlay from './LockedFeatureOverlay';

interface FeatureGateProps {
  feature: FeatureId;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradeCta?: boolean;
  onUpgradeClick?: () => void;
  renderMode?: 'hide' | 'disable' | 'overlay' | 'card';
  disabledTooltip?: string;
}

/**
 * FeatureGate Component (HA-108)
 * Wraps tier-restricted features with conditional rendering
 *
 * Render modes:
 * - 'hide': Completely hides the content if feature is unavailable
 * - 'disable': Shows content but disables interaction
 * - 'overlay': Shows blurred content with upgrade overlay
 * - 'card': Shows a locked feature card placeholder
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  fallback,
  showUpgradeCta = true,
  onUpgradeClick,
  renderMode = 'hide',
  disabledTooltip,
}) => {
  const { checkFeature } = useLicenseEntitlement();
  const featureCheck = checkFeature(feature);
  const featureConfig = featureConfigs[feature];

  // Feature is available, render children normally
  if (featureCheck.isAvailable) {
    return children;
  }

  // Feature is not available - handle based on renderMode
  const defaultTooltip =
    disabledTooltip || `Requires ${featureCheck.requiredTierName} tier to access this feature`;

  switch (renderMode) {
    case 'hide':
      return fallback || null;

    case 'disable':
      return (
        <Tooltip title={defaultTooltip} arrow>
          <Box
            sx={{
              opacity: 0.5,
              pointerEvents: 'none',
              cursor: 'not-allowed',
              '& *': {
                pointerEvents: 'none',
              },
            }}
          >
            {children}
          </Box>
        </Tooltip>
      );

    case 'overlay':
      return (
        <LockedFeatureOverlay
          requiredTier={featureCheck.requiredTier}
          featureName={featureConfig?.name}
          description={featureConfig?.description}
          showUpgradeButton={showUpgradeCta}
          onUpgradeClick={onUpgradeClick}
          variant="overlay"
        >
          {children}
        </LockedFeatureOverlay>
      );

    case 'card':
      return (
        <LockedFeatureOverlay
          requiredTier={featureCheck.requiredTier}
          featureName={featureConfig?.name}
          description={featureConfig?.description}
          showUpgradeButton={showUpgradeCta}
          onUpgradeClick={onUpgradeClick}
          variant="card"
        />
      );

    default:
      return fallback || null;
  }
};

export interface FeatureButtonGateProps {
  feature: FeatureId;
  // HA-373: cloneElement below merges { disabled: true } in -- children must
  // declare it accepts that prop for the overload to resolve.
  children: React.ReactElement<{ disabled?: boolean }>;
  disabledTooltip?: string;
}

/**
 * FeatureButtonGate Component
 * Specifically for gating buttons - disables and shows tooltip
 */
export const FeatureButtonGate: React.FC<FeatureButtonGateProps> = ({
  feature,
  children,
  disabledTooltip,
}) => {
  const { checkFeature } = useLicenseEntitlement();
  const featureCheck = checkFeature(feature);

  if (featureCheck.isAvailable) {
    return children;
  }

  const tooltip = disabledTooltip || `Requires ${featureCheck.requiredTierName} tier`;

  // Clone the button element and add disabled prop
  const disabledButton = React.cloneElement(children, {
    disabled: true,
  });

  return (
    <Tooltip title={tooltip} arrow>
      <span>{disabledButton}</span>
    </Tooltip>
  );
};

export interface FeatureFormFieldGateProps {
  feature: FeatureId;
  // HA-373: cloneElement below merges InputProps/disabled/helperText in, and
  // reads children.props.InputProps -- children must declare these props for
  // the overload to resolve and the property access to be type-safe.
  children: React.ReactElement<{
    InputProps?: Record<string, unknown>;
    disabled?: boolean;
    helperText?: string;
  }>;
  readOnlyTooltip?: string;
}

/**
 * FeatureFormFieldGate Component
 * For gating form fields - makes them read-only with tooltip
 */
export const FeatureFormFieldGate: React.FC<FeatureFormFieldGateProps> = ({
  feature,
  children,
  readOnlyTooltip,
}) => {
  const { checkFeature } = useLicenseEntitlement();
  const featureCheck = checkFeature(feature);

  if (featureCheck.isAvailable) {
    return children;
  }

  const tooltip =
    readOnlyTooltip || `Upgrade to ${featureCheck.requiredTierName} to edit this field`;

  // Clone the form field and add InputProps for read-only
  const readOnlyField = React.cloneElement(children, {
    InputProps: {
      ...children.props.InputProps,
      readOnly: true,
    },
    disabled: true,
    helperText: `Requires ${featureCheck.requiredTierName}`,
  });

  return (
    <Tooltip title={tooltip} arrow>
      <span style={{ display: 'block' }}>{readOnlyField}</span>
    </Tooltip>
  );
};

export default FeatureGate;
